import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Switch,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Svg, { Path } from 'react-native-svg';
import { PageContainer, Navbar, Breadcrumb, ConfirmModal } from '../../components';
import { COLORS, FONTS } from '../../constants/config';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';

// Back Icon
const BackIcon = ({ size = 24, color = COLORS.iconDefault }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M19 12H5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 19L5 12L12 5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// User Icon
const UserIcon = ({ size = 20, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Moon Icon
const MoonIcon = ({ size = 20, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Bell Icon
const BellIcon = ({ size = 20, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Logout Icon
const LogoutIcon = ({ size = 20, color = COLORS.dangerHover }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 17L21 12L16 7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M21 12H9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Globe/Language Icon
const GlobeIcon = ({ size = 20, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 12H22" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

export default function SettingsScreen() {
  const navigation = useNavigation();
  const { user, logout } = useAuth();
  const { showSuccess } = useNotification();
  const { isDark, toggleTheme, colors, themeMode, setThemeMode } = useTheme();
  const { language, changeLanguage, t } = useLanguage();
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutModal(false);
    await logout();
    showSuccess(t('auth.logoutSuccess'));
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const getUserTypeDisplay = () => {
    const userType = user?.profile?.user_type;
    switch (userType) {
      case 'superuser':
        return t('userTypes.superuser');
      case 'strategy_team':
        return t('userTypes.strategyTeam');
      case 'org_employee':
        return t('userTypes.orgEmployee');
      case 'executive':
        return t('userTypes.executive');
      default:
        return userType || t('common.notSpecified');
    }
  };

  return (
    <PageContainer backgroundColor={colors.background}>
      <Navbar />
      <Breadcrumb
        items={[
          { label: t('navigation.home') },
          { label: t('settings.title') },
        ]}
      />
      <ScrollView style={[styles.container, { backgroundColor: colors.background }]} showsVerticalScrollIndicator={false}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
            <BackIcon size={20} color={colors.iconDefault} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: colors.gray900 }]}>{t('settings.title')}</Text>
        </View>

        {/* User Info Section */}
        <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
          <View style={[styles.sectionHeader, { borderBottomColor: colors.gray200 }]}>
            <UserIcon size={20} color={colors.primary} />
            <Text style={[styles.sectionTitle, { color: colors.gray900 }]}>{t('settings.accountInfo')}</Text>
          </View>
          <View style={[styles.infoRow, { borderBottomColor: colors.gray100 }]}>
            <Text style={[styles.infoValue, { color: colors.gray900 }]}>{user?.first_name} {user?.last_name}</Text>
            <Text style={[styles.infoLabel, { color: colors.textMuted }]}>{t('common.name')}</Text>
          </View>
          <View style={[styles.infoRow, { borderBottomColor: colors.gray100 }]}>
            <Text style={[styles.infoValue, { color: colors.gray900 }]}>{user?.username}</Text>
            <Text style={[styles.infoLabel, { color: colors.textMuted }]}>{t('settings.username')}</Text>
          </View>
          <View style={[styles.infoRow, { borderBottomColor: colors.gray100 }]}>
            <Text style={[styles.infoValue, { color: colors.gray900 }]}>{user?.email}</Text>
            <Text style={[styles.infoLabel, { color: colors.textMuted }]}>{t('common.email')}</Text>
          </View>
          <View style={[styles.infoRow, { borderBottomColor: colors.gray100 }]}>
            <Text style={[styles.infoValue, { color: colors.gray900 }]}>{getUserTypeDisplay()}</Text>
            <Text style={[styles.infoLabel, { color: colors.textMuted }]}>{t('settings.accountType')}</Text>
          </View>
          {user?.profile?.organization_name && (
            <View style={[styles.infoRow, { borderBottomColor: colors.gray100 }]}>
              <Text style={[styles.infoValue, { color: colors.gray900 }]}>{user.profile.organization_name}</Text>
              <Text style={[styles.infoLabel, { color: colors.textMuted }]}>{t('settings.organization')}</Text>
            </View>
          )}
        </View>

        {/* Appearance Section */}
        <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
          <View style={[styles.sectionHeader, { borderBottomColor: colors.gray200 }]}>
            <MoonIcon size={20} color={colors.primary} />
            <Text style={[styles.sectionTitle, { color: colors.gray900 }]}>{t('settings.appearance')}</Text>
          </View>
          <View style={styles.settingRow}>
            <Switch
              value={isDark}
              onValueChange={toggleTheme}
              trackColor={{ false: colors.slate200, true: colors.primary }}
              thumbColor={colors.white}
            />
            <Text style={[styles.settingLabel, { color: colors.gray900 }]}>{t('settings.darkMode')}</Text>
          </View>
          <Text style={[styles.settingDescription, { color: colors.textLight }]}>
            {isDark ? t('settings.darkModeEnabled') : t('settings.lightModeEnabled')}
          </Text>
        </View>

        {/* Language Section */}
        <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
          <View style={[styles.sectionHeader, { borderBottomColor: colors.gray200 }]}>
            <GlobeIcon size={20} color={colors.primary} />
            <Text style={[styles.sectionTitle, { color: colors.gray900 }]}>{t('settings.language')}</Text>
          </View>
          <View style={styles.languageChipsContainer}>
            <TouchableOpacity
              style={[
                styles.languageChip,
                {
                  backgroundColor: language === 'ar' ? colors.primary : colors.gray100,
                  borderColor: language === 'ar' ? colors.primary : colors.gray200,
                }
              ]}
              onPress={() => changeLanguage('ar')}
            >
              <Text style={[
                styles.languageChipText,
                { color: language === 'ar' ? colors.white : colors.gray700 }
              ]}>
                {t('settings.arabic')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.languageChip,
                {
                  backgroundColor: language === 'en' ? colors.primary : colors.gray100,
                  borderColor: language === 'en' ? colors.primary : colors.gray200,
                }
              ]}
              onPress={() => changeLanguage('en')}
            >
              <Text style={[
                styles.languageChipText,
                { color: language === 'en' ? colors.white : colors.gray700 }
              ]}>
                {t('settings.english')}
              </Text>
            </TouchableOpacity>
          </View>
          <Text style={[styles.settingDescription, { color: colors.textLight }]}>
            {language === 'ar' ? t('settings.arabicSelected') : t('settings.englishSelected')}
          </Text>
        </View>

        {/* Notifications Section */}
        <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
          <View style={[styles.sectionHeader, { borderBottomColor: colors.gray200 }]}>
            <BellIcon size={20} color={colors.primary} />
            <Text style={[styles.sectionTitle, { color: colors.gray900 }]}>{t('settings.notifications')}</Text>
          </View>
          <View style={styles.settingRow}>
            <Switch
              value={notificationsEnabled}
              onValueChange={setNotificationsEnabled}
              trackColor={{ false: colors.slate200, true: colors.primary }}
              thumbColor={colors.white}
            />
            <Text style={[styles.settingLabel, { color: colors.gray900 }]}>{t('settings.enableNotifications')}</Text>
          </View>
        </View>

        {/* Logout Button */}
        <TouchableOpacity style={[styles.logoutButton, { backgroundColor: colors.dangerLight, borderColor: '#FECACA' }]} onPress={handleLogout}>
          <LogoutIcon size={20} color={colors.dangerHover} />
          <Text style={[styles.logoutText, { color: colors.dangerHover }]}>{t('auth.logout')}</Text>
        </TouchableOpacity>

        {/* App Version */}
        <Text style={[styles.versionText, { color: colors.textLight }]}>{t('settings.version')} 1.0.0</Text>
      </ScrollView>

      {/* Logout Confirmation Modal */}
      <ConfirmModal
        visible={showLogoutModal}
        title={t('auth.logout')}
        message={t('auth.logoutConfirm')}
        confirmText={t('auth.logout')}
        cancelText={t('common.cancel')}
        type="danger"
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 24,
  },
  backBtn: {
    padding: 8,
    marginLeft: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontFamily: FONTS.bold,
    color: COLORS.gray900,
  },
  section: {
    backgroundColor: COLORS.white,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: COLORS.gray200,
    padding: 16,
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.gray200,
    gap: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: COLORS.gray900,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.gray100,
  },
  infoLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.textMuted,
  },
  infoValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: COLORS.gray900,
  },
  settingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  settingLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.gray900,
  },
  settingDescription: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: COLORS.textLight,
    textAlign: 'right',
    marginTop: 8,
  },
  languageChipsContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    paddingVertical: 8,
  },
  languageChip: {
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 20,
    borderWidth: 1,
    minWidth: 100,
    alignItems: 'center',
  },
  languageChipText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
  },
  logoutButton: {
    backgroundColor: COLORS.dangerLight,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#FECACA',  // Red-200 - specific danger border
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 8,
  },
  logoutText: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: COLORS.dangerHover,
  },
  versionText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: COLORS.textLight,
    textAlign: 'center',
    marginTop: 24,
    marginBottom: 32,
  },
});
